import { z } from "zod";

import { simpleDateSchema } from "@/src/models/common-schema";
import { DatabaseDefaults } from "@/src/models/database-defaults";

/**
 * Where the alert applies. Mirrors the two surfaces most EMRs expose:
 *   - GLOBAL  : chart-level pop-up / banner (FHIR `Flag` resource).
 *   - BILLING : account-level financial alert / collections hold.
 */
export enum PatientAlertCategory {
  GLOBAL = "global",
  BILLING = "billing",
}

/**
 * Normalized severity. EMRs that don't surface priority leave this null.
 */
export enum PatientAlertPriority {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

/**
 * Normalized alert type. The DB column is free-text TEXT to accommodate
 * arbitrary vendor labels, but this enum captures the common buckets,
 * aligned to FHIR `Flag` categories (http://terminology.hl7.org/CodeSystem/flag-category)
 * with extra EMR-flavored values.
 *
 * Adapters should `normalizePatientAlertType()` before writing.
 */
export enum PatientAlertType {
  ALLERGY = "allergy",
  CLINICAL = "clinical",
  BEHAVIORAL = "behavioral",
  CONTACT = "contact",
  DIET = "diet",
  DRUG = "drug",
  LAB = "lab",
  SAFETY = "safety",
  ADMIN = "admin",
  ADVANCE_DIRECTIVE = "advance_directive",
  FINANCIAL = "financial",
  RESEARCH = "research",
  NOTE = "note",
  OTHER = "other",
}

const TYPE_ALIASES: Record<string, PatientAlertType> = {
  "allergy": PatientAlertType.ALLERGY,
  "allergies": PatientAlertType.ALLERGY,
  "clinical": PatientAlertType.CLINICAL,
  "behavioral": PatientAlertType.BEHAVIORAL,
  "behaviour": PatientAlertType.BEHAVIORAL,
  "contact": PatientAlertType.CONTACT,
  "diet": PatientAlertType.DIET,
  "drug": PatientAlertType.DRUG,
  "medication": PatientAlertType.DRUG,
  "lab": PatientAlertType.LAB,
  "safety": PatientAlertType.SAFETY,
  "admin": PatientAlertType.ADMIN,
  "administrative": PatientAlertType.ADMIN,
  "advance directive": PatientAlertType.ADVANCE_DIRECTIVE,
  "advance-directive": PatientAlertType.ADVANCE_DIRECTIVE,
  "advance_directive": PatientAlertType.ADVANCE_DIRECTIVE,
  "financial": PatientAlertType.FINANCIAL,
  "billing": PatientAlertType.FINANCIAL,
  "research": PatientAlertType.RESEARCH,
  "note": PatientAlertType.NOTE,
  "notes": PatientAlertType.NOTE,
};

export function normalizePatientAlertType(raw: string | null | undefined): PatientAlertType {
  if (!raw) return PatientAlertType.OTHER;

  return TYPE_ALIASES[raw.trim().toLowerCase()] ?? PatientAlertType.OTHER;
}

const PRIORITY_ALIASES: Record<string, PatientAlertPriority> = {
  high: PatientAlertPriority.HIGH,
  h: PatientAlertPriority.HIGH,
  medium: PatientAlertPriority.MEDIUM,
  med: PatientAlertPriority.MEDIUM,
  m: PatientAlertPriority.MEDIUM,
  low: PatientAlertPriority.LOW,
  l: PatientAlertPriority.LOW,
};

export function normalizePatientAlertPriority(raw: string | null | undefined): PatientAlertPriority | null {
  if (!raw) return null;

  return PRIORITY_ALIASES[raw.trim().toLowerCase()] ?? null;
}

export const patientAlertSchema = z.object({
  patientId: z.string().uuid(),
  category: z.nativeEnum(PatientAlertCategory),
  alertType: z.string(),
  name: z.string(),
  notes: z.string().nullable(),
  priority: z.nativeEnum(PatientAlertPriority).nullable(),
  expiryDate: simpleDateSchema.nullable(),
  ehrMetadata: z.unknown().nullable(),
});

export type PatientAlert = z.infer<typeof patientAlertSchema> & DatabaseDefaults;

export const patientAlertShape: PatientAlert = {
  id: "",
  groupId: "",
  patientId: "",
  category: PatientAlertCategory.GLOBAL,
  alertType: PatientAlertType.OTHER,
  name: "",
  notes: null,
  priority: null,
  expiryDate: null,
  ehrMetadata: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};
